import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { 
  Plus, Trash2, Pencil, Wallet, TrendingDown, PiggyBank, 
  Calendar as CalendarIcon, Home, UtensilsCrossed, Heart,
  Car, ShoppingBag, Zap, Smartphone, Plane, GraduationCap,
  Gift, MoreHorizontal, Target, Filter
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const CHART_COLORS = ["#F43F5E", "#10B981", "#F59E0B", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#84CC16"];

const CATEGORY_ICONS = {
  home: Home,
  food: UtensilsCrossed,
  health: Heart,
  transport: Car,
  shopping: ShoppingBag,
  utilities: Zap,
  phone: Smartphone,
  travel: Plane,
  education: GraduationCap,
  gifts: Gift,
  other: MoreHorizontal,
  receipt: Wallet
};

const ICON_OPTIONS = [
  { value: "home", label: "Vivienda", Icon: Home },
  { value: "food", label: "Comida", Icon: UtensilsCrossed },
  { value: "health", label: "Salud", Icon: Heart },
  { value: "transport", label: "Transporte", Icon: Car },
  { value: "shopping", label: "Compras", Icon: ShoppingBag },
  { value: "utilities", label: "Servicios", Icon: Zap },
  { value: "phone", label: "Teléfono", Icon: Smartphone },
  { value: "travel", label: "Viajes", Icon: Plane },
  { value: "education", label: "Educación", Icon: GraduationCap },
  { value: "gifts", label: "Regalos", Icon: Gift },
  { value: "other", label: "Otros", Icon: MoreHorizontal },
];

const COLORS = ["#F43F5E", "#10B981", "#F59E0B", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#84CC16", "#EF4444", "#0EA5E9"];

const PersonalFinancePage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [budget, setBudget] = useState(null);
  const [summary, setSummary] = useState(null);
  const [categoryBudgets, setCategoryBudgets] = useState([]);
  
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Dialogs
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryBudgetDialogOpen, setCategoryBudgetDialogOpen] = useState(false);
  const [editingCategoryBudget, setEditingCategoryBudget] = useState(null);
  
  // Forms
  const [newCategory, setNewCategory] = useState({ name: "", color: "#F43F5E", icon: "receipt" });
  const [newBudget, setNewBudget] = useState("");
  const [categoryBudgetAmount, setCategoryBudgetAmount] = useState("");
  const [transForm, setTransForm] = useState({
    date: new Date(),
    description: "",
    amount: "",
    category_id: "",
    notes: ""
  });
  
  // Filter
  const [filterCategory, setFilterCategory] = useState("all");

  const months = [
    { value: 1, label: "Enero" }, { value: 2, label: "Febrero" }, { value: 3, label: "Marzo" },
    { value: 4, label: "Abril" }, { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
    { value: 7, label: "Julio" }, { value: 8, label: "Agosto" }, { value: 9, label: "Septiembre" },
    { value: 10, label: "Octubre" }, { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" }
  ];
  const years = [2024, 2025, 2026];

  // Filtered transactions
  const filteredTransactions = filterCategory === "all" 
    ? transactions 
    : transactions.filter(t => t.category_id === filterCategory);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [catRes, transRes, budgetRes, summaryRes, catBudgetsRes] = await Promise.all([
        axios.get(`${API}/personal/categories`, getAuthHeaders()),
        axios.get(`${API}/personal/transactions?month=${selectedMonth}&year=${selectedYear}`, getAuthHeaders()),
        axios.get(`${API}/personal/budget?month=${selectedMonth}&year=${selectedYear}`, getAuthHeaders()),
        axios.get(`${API}/personal/summary?month=${selectedMonth}&year=${selectedYear}`, getAuthHeaders()),
        axios.get(`${API}/personal/category-budgets?month=${selectedMonth}&year=${selectedYear}`, getAuthHeaders())
      ]);
      setCategories(catRes.data);
      setTransactions(transRes.data);
      setBudget(budgetRes.data);
      setSummary(summaryRes.data);
      setCategoryBudgets(catBudgetsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Solo administradores pueden acceder");
      } else {
        toast.error("Error al cargar datos");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);
  };

  // Category handlers
  const resetCategoryForm = () => {
    setNewCategory({ name: "", color: "#F43F5E", icon: "receipt" });
    setEditingCategory(null);
  };

  const handleOpenCategoryDialog = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setNewCategory({ name: cat.name, color: cat.color, icon: cat.icon });
    } else {
      resetCategoryForm();
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API}/personal/categories/${editingCategory.id}`, newCategory, getAuthHeaders());
        toast.success("Categoría actualizada");
      } else {
        await axios.post(`${API}/personal/categories`, newCategory, getAuthHeaders());
        toast.success("Categoría creada");
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error(editingCategory ? "Error al actualizar categoría" : "Error al crear categoría");
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!confirm("¿Eliminar esta categoría?")) return;
    try {
      await axios.delete(`${API}/personal/categories/${id}`, getAuthHeaders());
      toast.success("Categoría eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  // Budget handlers
  const handleSetBudget = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/personal/budget`, {
        month: selectedMonth,
        year: selectedYear,
        total_budget: parseFloat(newBudget)
      }, getAuthHeaders());
      toast.success("Presupuesto actualizado");
      setBudgetDialogOpen(false);
      setNewBudget("");
      fetchData();
    } catch (error) {
      toast.error("Error al guardar presupuesto");
    }
  };

  // Category Budget handlers
  const handleOpenCategoryBudgetDialog = (catBudget) => {
    setEditingCategoryBudget(catBudget);
    setCategoryBudgetAmount(catBudget.budget_amount > 0 ? catBudget.budget_amount.toString() : "");
    setCategoryBudgetDialogOpen(true);
  };

  const handleSaveCategoryBudget = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/personal/category-budgets`, {
        category_id: editingCategoryBudget.category_id,
        month: selectedMonth,
        year: selectedYear,
        budget_amount: parseFloat(categoryBudgetAmount)
      }, getAuthHeaders());
      toast.success("Presupuesto de categoría actualizado");
      setCategoryBudgetDialogOpen(false);
      setCategoryBudgetAmount("");
      setEditingCategoryBudget(null);
      fetchData();
    } catch (error) {
      toast.error("Error al guardar presupuesto");
    }
  };

  // Transaction handlers
  const resetTransForm = () => {
    setTransForm({ date: new Date(), description: "", amount: "", category_id: "", notes: "" });
    setEditingTransaction(null);
  };

  const handleEditTransaction = (trans) => {
    setEditingTransaction(trans);
    setTransForm({
      date: parseISO(trans.date),
      description: trans.description,
      amount: trans.amount.toString(),
      category_id: trans.category_id,
      notes: trans.notes || ""
    });
    setTransactionDialogOpen(true);
  };

  const handleSaveTransaction = async (e) => {
    e.preventDefault();
    try {
      const data = {
        date: format(transForm.date, "yyyy-MM-dd"),
        description: transForm.description,
        amount: parseFloat(transForm.amount),
        category_id: transForm.category_id,
        notes: transForm.notes
      };
      
      if (editingTransaction) {
        await axios.put(`${API}/personal/transactions/${editingTransaction.id}`, data, getAuthHeaders());
        toast.success("Transacción actualizada");
      } else {
        await axios.post(`${API}/personal/transactions`, data, getAuthHeaders());
        toast.success("Gasto registrado");
      }
      
      setTransactionDialogOpen(false);
      resetTransForm();
      fetchData();
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!confirm("¿Eliminar este gasto?")) return;
    try {
      await axios.delete(`${API}/personal/transactions/${id}`, getAuthHeaders());
      toast.success("Gasto eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-[#FECDD3] rounded-lg p-3 shadow-lg">
          <p className="text-[#4A4A4A] font-medium">{payload[0].name}</p>
          <p className="text-[#F43F5E] font-mono">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner w-8 h-8"></div>
        </div>
      </Layout>
    );
  }

  const progressPercent = summary?.percentage_used || 0;
  const progressColor = progressPercent > 100 ? "#EF4444" : progressPercent > 80 ? "#F59E0B" : "#10B981";

  return (
    <Layout>
      <div className="space-y-6" data-testid="personal-finance-page">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Finanzas Personales</h1>
            <p className="text-[#6B7280]">Control de gastos personales y presupuesto mensual</p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="bg-white border-[#FECDD3] text-[#4A4A4A]">
                <SelectValue placeholder="Mes" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#FECDD3]">
                {months.map((m) => (
                  <SelectItem key={m.value} value={m.value.toString()} className="text-[#4A4A4A]">
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="bg-white border-[#FECDD3] text-[#4A4A4A]">
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#FECDD3]">
                {years.map((y) => (
                  <SelectItem key={y} value={y.toString()} className="text-[#4A4A4A]">
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Budget Card */}
        <Card className="bg-gradient-to-r from-[#FEE2E2] to-[#FECDD3] border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-xl bg-white/80 flex items-center justify-center">
                    <PiggyBank className="w-6 h-6 text-[#F43F5E]" />
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-sm">Presupuesto {months.find(m => m.value === selectedMonth)?.label}</p>
                    <p className="text-3xl font-bold text-[#4A4A4A] font-mono">{formatCurrency(summary?.total_budget)}</p>
                  </div>
                </div>
                
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#6B7280]">Gastado: <span className="font-mono text-[#F43F5E]">{formatCurrency(summary?.total_spent)}</span></span>
                    <span className="text-[#6B7280]">Restante: <span className={`font-mono ${summary?.remaining >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{formatCurrency(summary?.remaining)}</span></span>
                  </div>
                  <div className="h-3 bg-white/50 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${Math.min(progressPercent, 100)}%`,
                        backgroundColor: progressColor
                      }}
                    />
                  </div>
                  <p className="text-xs text-[#6B7280] mt-1 text-right">{progressPercent.toFixed(1)}% usado</p>
                </div>
              </div>
              
              <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-[#F43F5E] hover:bg-white/90 shadow-md">
                    <Pencil className="w-4 h-4 mr-2" />
                    Editar Presupuesto
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-[#FECDD3]">
                  <DialogHeader>
                    <DialogTitle>Definir Presupuesto Mensual</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSetBudget} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Presupuesto para {months.find(m => m.value === selectedMonth)?.label} {selectedYear}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newBudget}
                        onChange={(e) => setNewBudget(e.target.value)}
                        placeholder="Ej: 15000"
                        required
                        className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] font-mono"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button type="button" variant="outline" onClick={() => setBudgetDialogOpen(false)} className="flex-1">Cancelar</Button>
                      <Button type="submit" className="flex-1 bg-[#F43F5E] hover:bg-[#E11D48] text-white">Guardar</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-[#FECDD3] card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-sm">Total Gastado</p>
                  <p className="text-2xl font-bold text-[#F43F5E] font-mono mt-1">{formatCurrency(summary?.total_spent)}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#FEE2E2] flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-[#F43F5E]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-sm">Disponible</p>
                  <p className={`text-2xl font-bold font-mono mt-1 ${summary?.remaining >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                    {formatCurrency(summary?.remaining)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#D1FAE5] flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-[#10B981]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-sm">Transacciones</p>
                  <p className="text-2xl font-bold text-[#4A4A4A] font-mono mt-1">{summary?.transaction_count || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                  <CalendarIcon className="w-6 h-6 text-[#3B82F6]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Category Filter Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white rounded-lg border border-[#FECDD3] p-4">
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-[#F43F5E]" />
            <span className="text-[#4A4A4A] font-medium">Filtrar por categoría:</span>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[200px] bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]">
                <SelectValue placeholder="Seleccionar categoría" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#FECDD3]">
                <SelectItem value="all" className="text-[#4A4A4A]">
                  Todas las categorías
                </SelectItem>
                {categories.map((cat) => {
                  const IconComp = CATEGORY_ICONS[cat.icon] || Wallet;
                  return (
                    <SelectItem key={cat.id} value={cat.id} className="text-[#4A4A4A]">
                      <div className="flex items-center gap-2">
                        <IconComp className="w-4 h-4" style={{ color: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {filterCategory !== "all" && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setFilterCategory("all")}
                className="text-[#F43F5E] hover:text-[#E11D48] hover:bg-[#FEE2E2]"
              >
                Limpiar filtro
              </Button>
            )}
          </div>
          {filterCategory !== "all" && (
            <span className="text-sm text-[#6B7280]">
              Mostrando {filteredTransactions.length} de {transactions.length} transacciones
            </span>
          )}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Transactions List */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-[#4A4A4A]">Gastos del Mes</h2>
              <Dialog open={transactionDialogOpen} onOpenChange={(open) => { setTransactionDialogOpen(open); if (!open) resetTransForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#F43F5E] hover:bg-[#E11D48] text-white btn-glow">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Gasto
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-[#FECDD3]">
                  <DialogHeader>
                    <DialogTitle>{editingTransaction ? "Editar Gasto" : "Registrar Gasto"}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveTransaction} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Fecha</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left bg-[#FEE2E2] border-[#FECDD3]">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(transForm.date, "PPP", { locale: es })}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-white border-[#FECDD3]">
                          <Calendar
                            mode="single"
                            selected={transForm.date}
                            onSelect={(date) => date && setTransForm({...transForm, date})}
                            locale={es}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Descripción</Label>
                      <Input
                        value={transForm.description}
                        onChange={(e) => setTransForm({...transForm, description: e.target.value})}
                        placeholder="Ej: Renta de departamento"
                        required
                        className="bg-[#FEE2E2] border-[#FECDD3]"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Monto</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={transForm.amount}
                        onChange={(e) => setTransForm({...transForm, amount: e.target.value})}
                        placeholder="0.00"
                        required
                        className="bg-[#FEE2E2] border-[#FECDD3] font-mono"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Categoría</Label>
                      <Select value={transForm.category_id} onValueChange={(v) => setTransForm({...transForm, category_id: v})}>
                        <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3]">
                          <SelectValue placeholder="Seleccionar categoría" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#FECDD3]">
                          {categories.map((cat) => {
                            const IconComp = CATEGORY_ICONS[cat.icon] || Wallet;
                            return (
                              <SelectItem key={cat.id} value={cat.id}>
                                <div className="flex items-center gap-2">
                                  <IconComp className="w-4 h-4" style={{ color: cat.color }} />
                                  {cat.name}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Notas (opcional)</Label>
                      <Textarea
                        value={transForm.notes}
                        onChange={(e) => setTransForm({...transForm, notes: e.target.value})}
                        placeholder="Notas adicionales..."
                        className="bg-[#FEE2E2] border-[#FECDD3] min-h-[60px]"
                      />
                    </div>

                    <div className="flex gap-3">
                      <Button type="button" variant="outline" onClick={() => setTransactionDialogOpen(false)} className="flex-1">Cancelar</Button>
                      <Button type="submit" className="flex-1 bg-[#F43F5E] hover:bg-[#E11D48] text-white">
                        {editingTransaction ? "Actualizar" : "Guardar"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {filteredTransactions.length === 0 ? (
              <Card className="bg-white border-[#FECDD3]">
                <CardContent className="p-8 text-center">
                  <Wallet className="w-12 h-12 mx-auto text-[#FECDD3] mb-4" />
                  {filterCategory !== "all" ? (
                    <>
                      <p className="text-[#6B7280]">No hay gastos en esta categoría</p>
                      <Button 
                        variant="link" 
                        onClick={() => setFilterCategory("all")}
                        className="text-[#F43F5E] mt-2"
                      >
                        Ver todas las categorías
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-[#6B7280]">No hay gastos registrados este mes</p>
                      <p className="text-sm text-[#9CA3AF]">Agrega tu primer gasto para comenzar</p>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredTransactions.map((t) => {
                  const IconComp = CATEGORY_ICONS[categories.find(c => c.id === t.category_id)?.icon] || Wallet;
                  return (
                    <Card key={t.id} className="bg-white border-[#FEE2E2] card-hover">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${t.category_color}20` }}
                          >
                            <IconComp className="w-5 h-5" style={{ color: t.category_color }} />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <p className="text-[#4A4A4A] font-medium truncate">{t.description}</p>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-[#6B7280]">{format(parseISO(t.date), "dd MMM", { locale: es })}</span>
                              <span className="text-[#FECDD3]">•</span>
                              <span style={{ color: t.category_color }}>{t.category_name}</span>
                            </div>
                          </div>
                          
                          <div className="text-right flex-shrink-0">
                            <p className="text-lg font-bold text-[#F43F5E] font-mono">-{formatCurrency(t.amount)}</p>
                          </div>
                          
                          <div className="flex gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditTransaction(t)}
                              className="h-8 w-8 p-0 text-[#6B7280] hover:text-[#4A4A4A]"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTransaction(t.id)}
                              className="h-8 w-8 p-0 text-[#6B7280] hover:text-[#F43F5E]"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Chart */}
            {summary?.expenses_by_category?.length > 0 && (
              <Card className="bg-white border-[#FECDD3]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#4A4A4A] text-base">Por Categoría</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={summary.expenses_by_category}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={70}
                          innerRadius={40}
                        >
                          {summary.expenses_by_category.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2 mt-2">
                    {summary.expenses_by_category.slice(0, 5).map((cat, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          <span className="text-[#6B7280] truncate">{cat.name}</span>
                        </div>
                        <span className="text-[#4A4A4A] font-mono">{formatCurrency(cat.value)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Category Budgets */}
            {categoryBudgets.length > 0 && (
              <Card className="bg-white border-[#FECDD3]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-[#4A4A4A] text-base flex items-center gap-2">
                      <Target className="w-4 h-4 text-[#F43F5E]" />
                      Presupuestos
                    </CardTitle>
                  </div>
                  <CardDescription className="text-[#9CA3AF] text-xs">
                    Click en una categoría para editar
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {categoryBudgets.map((catBudget) => {
                      const IconComp = CATEGORY_ICONS[catBudget.category_icon] || Wallet;
                      const progressColor = catBudget.percentage_used > 100 
                        ? "#EF4444" 
                        : catBudget.percentage_used > 80 
                          ? "#F59E0B" 
                          : "#10B981";
                      
                      return (
                        <div 
                          key={catBudget.category_id} 
                          className="p-2 rounded-lg hover:bg-[#FEE2E2] cursor-pointer transition-colors"
                          onClick={() => handleOpenCategoryBudgetDialog(catBudget)}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-6 h-6 rounded flex items-center justify-center"
                                style={{ backgroundColor: `${catBudget.category_color}20` }}
                              >
                                <IconComp className="w-3 h-3" style={{ color: catBudget.category_color }} />
                              </div>
                              <span className="text-[#4A4A4A] text-sm font-medium">{catBudget.category_name}</span>
                            </div>
                            <Pencil className="w-3 h-3 text-[#9CA3AF]" />
                          </div>
                          
                          {catBudget.budget_amount > 0 ? (
                            <>
                              <div className="h-1.5 bg-[#FEE2E2] rounded-full overflow-hidden mb-1">
                                <div 
                                  className="h-full rounded-full transition-all"
                                  style={{ 
                                    width: `${Math.min(catBudget.percentage_used, 100)}%`,
                                    backgroundColor: progressColor
                                  }}
                                />
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-[#6B7280]">
                                  {formatCurrency(catBudget.spent)} / {formatCurrency(catBudget.budget_amount)}
                                </span>
                                <span style={{ color: progressColor }}>
                                  {catBudget.percentage_used.toFixed(0)}%
                                </span>
                              </div>
                            </>
                          ) : (
                            <p className="text-xs text-[#9CA3AF] italic">Sin presupuesto asignado</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Categories */}
            <Card className="bg-white border-[#FECDD3]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-[#4A4A4A] text-base">Categorías</CardTitle>
                  <Dialog open={categoryDialogOpen} onOpenChange={(open) => { setCategoryDialogOpen(open); if (!open) resetCategoryForm(); }}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleOpenCategoryDialog()}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-[#FECDD3]">
                      <DialogHeader>
                        <DialogTitle>{editingCategory ? "Editar Categoría" : "Nueva Categoría"}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSaveCategory} className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-[#6B7280]">Nombre</Label>
                          <Input
                            value={newCategory.name}
                            onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                            placeholder="Ej: Renta"
                            required
                            className="bg-[#FEE2E2] border-[#FECDD3]"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-[#6B7280]">Icono</Label>
                          <div className="grid grid-cols-6 gap-2">
                            {ICON_OPTIONS.map(({ value, Icon }) => (
                              <button
                                key={value}
                                type="button"
                                onClick={() => setNewCategory({...newCategory, icon: value})}
                                className={`p-2 rounded-lg transition-all ${newCategory.icon === value ? 'bg-[#F43F5E] text-white' : 'bg-[#FEE2E2] text-[#6B7280] hover:bg-[#FECDD3]'}`}
                              >
                                <Icon className="w-5 h-5" />
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-[#6B7280]">Color</Label>
                          <div className="flex flex-wrap gap-2">
                            {COLORS.map((color) => (
                              <button
                                key={color}
                                type="button"
                                onClick={() => setNewCategory({...newCategory, color})}
                                className={`w-8 h-8 rounded-lg transition-transform ${newCategory.color === color ? 'ring-2 ring-offset-2 ring-[#4A4A4A] scale-110' : ''}`}
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)} className="flex-1">Cancelar</Button>
                          <Button type="submit" className="flex-1 bg-[#F43F5E] hover:bg-[#E11D48] text-white">
                            {editingCategory ? "Actualizar" : "Guardar"}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {categories.length === 0 ? (
                  <p className="text-sm text-[#9CA3AF] text-center py-4">Sin categorías. Crea una para empezar.</p>
                ) : (
                  <div className="space-y-2">
                    {categories.map((cat) => {
                      const IconComp = CATEGORY_ICONS[cat.icon] || Wallet;
                      return (
                        <div key={cat.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-[#FEE2E2] group">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${cat.color}20` }}
                            >
                              <IconComp className="w-4 h-4" style={{ color: cat.color }} />
                            </div>
                            <span className="text-[#4A4A4A] text-sm">{cat.name}</span>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenCategoryDialog(cat)}
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-[#6B7280] hover:text-[#4A4A4A]"
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCategory(cat.id)}
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-[#6B7280] hover:text-[#F43F5E]"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Category Budget Dialog */}
        <Dialog open={categoryBudgetDialogOpen} onOpenChange={(open) => { 
          setCategoryBudgetDialogOpen(open); 
          if (!open) {
            setEditingCategoryBudget(null);
            setCategoryBudgetAmount("");
          }
        }}>
          <DialogContent className="bg-white border-[#FECDD3]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingCategoryBudget && (
                  <>
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${editingCategoryBudget.category_color}20` }}
                    >
                      {(() => {
                        const IconComp = CATEGORY_ICONS[editingCategoryBudget.category_icon] || Wallet;
                        return <IconComp className="w-4 h-4" style={{ color: editingCategoryBudget.category_color }} />;
                      })()}
                    </div>
                    Presupuesto: {editingCategoryBudget.category_name}
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveCategoryBudget} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#6B7280]">
                  Presupuesto para {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={categoryBudgetAmount}
                  onChange={(e) => setCategoryBudgetAmount(e.target.value)}
                  placeholder="Ej: 5000"
                  required
                  className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] font-mono"
                />
              </div>
              
              {editingCategoryBudget && editingCategoryBudget.spent > 0 && (
                <div className="bg-[#FEF3C7] rounded-lg p-3 text-sm">
                  <p className="text-[#B45309]">
                    Ya has gastado <strong className="font-mono">{formatCurrency(editingCategoryBudget.spent)}</strong> en esta categoría este mes.
                  </p>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setCategoryBudgetDialogOpen(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-[#F43F5E] hover:bg-[#E11D48] text-white">
                  Guardar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PersonalFinancePage;
